/**
 * Safety gate list — `GET /safety-gates`.
 *
 * Migrated onto the CoOrdio list-page pattern (see `ProjectListPage.tsx`, the reference): the grid
 * owns paging, search, sorting and filtering, so the page keeps only the column plan, the row
 * actions, one `DeleteDialog`, and the single piece of refetch state (`listVersion`).
 *
 * ── Column plan ──────────────────────────────────────────────────────────────────────────────────
 *  field             | header    | width | cell type          | filter
 *  ------------------|-----------|-------|--------------------|----------------------------------
 *  name              | Name      |  24%  | text (clickable)   | toolbar search (searchTerm)
 *  actionPattern     | Pattern   |  22%  | text (monospace)   | —
 *  severity          | Severity  |  12%  | custom → Chip      | popover select (severity)
 *  requiresApproval  | Approval  |  14%  | custom → Chip      | —
 *  isActive          | Status    |  12%  | custom → Chip      | status tabs (synthetic → isActive)
 *  updatedAt         | Updated   |  16%  | date (locale)      | —
 *
 * Dropped (present on the old MUI table): `description` (was a muted second line under the name —
 * six columns is the cap and it is on the edit form), `requiredApprovers` and
 * `autoApprovalTimeoutMinutes` (approval mechanics, only meaningful once you already care about a
 * specific gate), `createdAt` (`updatedAt` is the one that moves), `approverRolesJson` /
 * `conditionJson` (raw JSON).
 *
 * Service filters NOT offered: `safetyGateService.getSafetyGates` accepts only `searchTerm`,
 * `severity` and `isActive`, all of which are wired above. There is no name-only filter and no sort
 * parameter at all, which is why no column is marked `sortable`.
 */

import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Button,
  CordioAbpPagedDataTable,
  Chip,
  ShellHeader,
  type ChipColor,
  type CordioDataTableAction,
  type CordioDataTableColumn,
  type CordioDataTableListToolbarConfig,
} from '@coordio/ui';
import { Plus, Power, PowerOff, Trash2 } from 'lucide-react';

import { createCortexPagedDataSource } from '../components/data-display/cortexPagedDataSource';
import { DeleteDialog, useDeleteDialog } from '../components/delete-dialog';
import {
  safetyGateService,
  SafetyGateSeverity,
  type SafetyGateDto,
} from '../services/safetyGateService';
import { useNotificationStore } from '../store/notificationStore';
import { ROUTES } from '../utils/routes';

import './SafetyGatesListPage.scss';

/**
 * Quick-filter tabs.
 *
 * `value` is load-bearing twice: the grid writes it to `paramKey`, and it builds each tab's test id
 * as `cordio-datatable-status-tab-<value>`. GRID-13 asserts on `-all` / `-true` / `-false`, so the
 * All tab must carry the literal `'all'` — an empty value would emit
 * `cordio-datatable-status-tab-` and the checklist would select nothing.
 *
 * That is why `paramKey` is the synthetic `status` rather than `isActive`: `'all'` is a UI concept,
 * not a value the API accepts, so {@link toSafetyGateQuery} maps it (and drops it for All).
 */
const STATUS_TAB_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'true' },
  { label: 'Inactive', value: 'false' },
] as const;

/** The synthetic toolbar key the status tabs write to. Never sent to the API as-is. */
const STATUS_TAB_PARAM_KEY = 'status';

const SEVERITY_FILTER_OPTIONS = Object.values(SafetyGateSeverity).map((severity) => ({
  label: severity,
  value: severity,
}));

/** Which confirmation the one dialog is currently staged for. */
type GateAction = 'delete' | 'activate' | 'deactivate';

/**
 * The staged row plus which action it was staged for.
 *
 * One object rather than two pieces of state so the dialog copy and the row can never disagree —
 * `useDeleteDialog` is generic precisely so a page can widen the staged item like this.
 */
type StagedGate = { row: SafetyGateDto; action: GateAction };

const severityChipColor = (severity: SafetyGateSeverity): ChipColor => {
  switch (severity) {
    case SafetyGateSeverity.Low:
      return 'green';
    case SafetyGateSeverity.Medium:
      return 'amber';
    case SafetyGateSeverity.High:
    case SafetyGateSeverity.Critical:
      return 'red';
    default:
      return 'default';
  }
};

/**
 * Turns the grid's query into the one `safetyGateService.getSafetyGates` accepts: the synthetic
 * `status` tab value becomes `isActive`, and All becomes "no filter at all" rather than a third
 * state.
 */
function toSafetyGateQuery(query: Record<string, unknown>): Record<string, unknown> {
  const { [STATUS_TAB_PARAM_KEY]: status, ...rest } = query;

  if (status === 'true' || status === 'false') {
    return { ...rest, isActive: status === 'true' };
  }
  return rest;
}

const gateEditPath = (id: string) => ROUTES.SAFETY_GATE_EDIT.replace(':id', id);

export const SafetyGatesListPage = () => {
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();

  /** Bumped after a mutation to rebuild `dataSource` and make the grid refetch. */
  const [listVersion, setListVersion] = useState(0);

  const { dialogRef, pendingItem, openDelete, closeDelete } = useDeleteDialog<StagedGate>();

  const dataSource = useMemo(
    () =>
      createCortexPagedDataSource<SafetyGateDto>((query) =>
        safetyGateService.getSafetyGates(toSafetyGateQuery(query)),
      ),
    // `listVersion` is a deliberate dependency: changing the identity of `dataSource` is the
    // documented way to make the grid reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [listVersion],
  );

  const columns = useMemo<ReadonlyArray<CordioDataTableColumn<SafetyGateDto>>>(
    () => [
      { field: 'name', header: 'Name', type: 'text', width: '24%', clickable: true },
      {
        field: 'actionPattern',
        header: 'Pattern',
        type: 'text',
        width: '22%',
        className: 'safety-gates-list__pattern',
        maxLength: 40,
        showReadMore: true,
      },
      {
        field: 'severity',
        header: 'Severity',
        type: 'custom',
        width: '12%',
        customCell: (row) => <Chip label={row.severity} color={severityChipColor(row.severity)} />,
      },
      {
        field: 'requiresApproval',
        header: 'Approval',
        type: 'custom',
        width: '14%',
        // "Required" / "Automatic" reads as the gate's behaviour; a bare Yes/No boolean cell needs
        // the header to carry the meaning, and this column is scanned more than it is read.
        customCell: (row) => (
          <Chip
            label={row.requiresApproval ? 'Required' : 'Automatic'}
            color={row.requiresApproval ? 'blue' : 'default'}
          />
        ),
      },
      {
        field: 'isActive',
        header: 'Status',
        type: 'custom',
        width: '12%',
        // A Chip rather than the built-in boolean cell: "Active/Inactive" is the vocabulary the
        // rest of the product uses, and it matches the status tabs above the grid.
        customCell: (row) => (
          <Chip label={row.isActive ? 'Active' : 'Inactive'} color={row.isActive ? 'green' : 'default'} />
        ),
      },
      { field: 'updatedAt', header: 'Updated', type: 'date', width: '16%', dateFormat: 'locale' },
    ],
    [],
  );

  const listToolbar = useMemo<CordioDataTableListToolbarConfig>(
    () => ({
      search: { placeholder: 'Search safety gates by name' },
      statusTabs: {
        paramKey: STATUS_TAB_PARAM_KEY,
        ariaLabel: 'Safety gate status filter',
        options: [...STATUS_TAB_OPTIONS],
      },
      filters: [
        { key: 'severity', label: 'Severity', type: 'select', options: SEVERITY_FILTER_OPTIONS },
      ],
      filterButtonLabel: 'Filter',
    }),
    [],
  );

  const stage = useCallback(
    (row: SafetyGateDto, action: GateAction) => openDelete({ row, action }),
    [openDelete],
  );

  const actions = useMemo<ReadonlyArray<CordioDataTableAction<SafetyGateDto>>>(
    () => [
      {
        icon: 'square-pen',
        tooltip: 'Edit safety gate',
        onClick: (row) => navigate(gateEditPath(row.id)),
      },
      {
        icon: 'check',
        tooltip: 'Activate safety gate',
        // `visible` rather than a disabled control: an activate action on an already-active gate
        // has nothing to say.
        visible: (row) => !row.isActive,
        onClick: (row) => stage(row, 'activate'),
      },
      {
        icon: 'x',
        tooltip: 'Deactivate safety gate',
        visible: (row) => row.isActive,
        onClick: (row) => stage(row, 'deactivate'),
      },
      {
        icon: 'trash-2',
        tooltip: 'Delete safety gate',
        className: 'safety-gates-list__action--destructive',
        onClick: (row) => stage(row, 'delete'),
      },
    ],
    [navigate, stage],
  );

  const handleConfirm = useCallback(async () => {
    if (!pendingItem) return;
    const { row, action } = pendingItem;

    if (action === 'delete') {
      await safetyGateService.deleteSafetyGate(row.id);
      addNotification('Safety gate deleted successfully', 'success');
    } else {
      const isActive = action === 'activate';
      // The update endpoint takes the whole gate, so the row is replayed with only `isActive`
      // changed — the same shape the old inline switch sent.
      await safetyGateService.updateSafetyGate(row.id, { ...row, isActive });
      addNotification(`Safety gate ${isActive ? 'activated' : 'deactivated'} successfully`, 'success');
    }

    setListVersion((v) => v + 1);
  }, [pendingItem, addNotification]);

  const action = pendingItem?.action;
  const isDelete = action === 'delete' || action === undefined;

  return (
    <div className="safety-gates-list" data-testid="safety-gate-list-page">
      <ShellHeader
        title="Safety Gates"
        description="Rules that pause a matching agent action until it is approved, or block it outright."
        data-testid="safety-gate-list-header"
        actions={
          <Button
            type="button"
            variant="default"
            size="sm"
            className="gap-2"
            data-testid="add-safety-gate-btn"
            onClick={() => navigate(ROUTES.SAFETY_GATE_NEW)}
          >
            <Plus className="h-4 w-4 shrink-0" aria-hidden />
            Create Safety Gate
          </Button>
        }
      />

      <div data-testid="safety-gate-list-grid">
        <CordioAbpPagedDataTable<SafetyGateDto>
          className="safety-gates-list__table"
          columns={columns}
          dataSource={dataSource}
          initialPageSize={25}
          maxBodyHeight="520px"
          listToolbar={listToolbar}
          actions={actions}
          onRowClick={(row) => navigate(gateEditPath(row.id))}
          onCellClick={({ row }) => navigate(gateEditPath(row.id))}
        />
      </div>

      <DeleteDialog
        ref={dialogRef}
        title={
          isDelete
            ? 'Delete safety gate?'
            : action === 'activate'
              ? 'Activate safety gate?'
              : 'Deactivate safety gate?'
        }
        entityLabel={pendingItem?.row.name}
        dialogIcon={
          isDelete ? (
            <Trash2 className="delete-dialog__icon" />
          ) : action === 'activate' ? (
            <Power className="delete-dialog__icon" />
          ) : (
            <PowerOff className="delete-dialog__icon" />
          )
        }
        description={
          isDelete
            ? 'Agent actions matching this pattern stop being gated. This cannot be undone.'
            : action === 'activate'
              ? 'Agent actions matching this pattern will be gated again from the next run.'
              : 'Agent actions matching this pattern will run ungated until the gate is activated again.'
        }
        confirmLabel={isDelete ? 'Delete' : action === 'activate' ? 'Activate' : 'Deactivate'}
        // Only the delete is destructive; toggling `isActive` is reversible in one click.
        confirmVariant={isDelete ? 'destructive' : 'default'}
        busyLabel={isDelete ? 'Deleting…' : action === 'activate' ? 'Activating…' : 'Deactivating…'}
        onConfirm={handleConfirm}
        onCancel={closeDelete}
      />
    </div>
  );
};
